<script setup lang="ts">
import type { FooterEntryFragment } from '#graphql-operations'
import type { BlocksInterface } from '~/components/organisms/BlocksRenderer.vue'

const { locale } = useI18n()

const variables = computed(() => ({
  site: [locale.value],
  section: ['footer'],
}))

const { data } = useAsyncGraphqlQuery('Entry', variables, {
  transform: input => input.data.entry as FooterEntryFragment,
})

const hasLauncherBlock = computed(() => data.value?.launcherBlock?.heading
  || data.value?.launcherBlock?.customLink
  || (data.value?.launcherBlock?.media?.length || 0) > 0,
)
</script>

<template>
  <footer class="theFooter">
    <LazyBlocksRenderer v-if="data && 'blocks' in data && data.blocks?.length" :blocks="(data.blocks as BlocksInterface[])" />
    <div class="container pb-4 pt-8 grid space-y-12.5 lg:pb-8 lg:pt-12.5 lg:min-h-125 lg:items-start lg:space-y-25">
      <div v-if="hasLauncherBlock" class="layout-grid">
        <div class="text-center col-span-full space-y-6 lg:col-span-6 lg:col-start-4">
          <h2 v-if="data?.launcherBlock?.heading" class="typo-serif-4">
            <Text :html="data?.launcherBlock?.heading" />
          </h2>
          <CraftSvg v-for="media in data?.launcherBlock?.media" :key="media!.id" class="mx-auto flex-none w-29 lg:w-57" :media />
          <ButtonLink :link="data?.launcherBlock?.customLink" />
        </div>
      </div>
      <div v-else class="container layout-grid">
        <div class="col-span-full">
          <SiteSwitchComponent class="mt-3 w-full lg:mt-0" :font-controlled="false" name="logotype" />
        </div>
      </div>
      <div class="layout-grid mt-auto lg:items-end">
        <div class="gap-1 col-span-3 lt-lg:flex lt-lg:flex-col">
          <Text v-if="data?.address" :html="data?.address" class="typo-sans-1" />
          <Text v-if="data?.copyrightAndGeneralInformation" :html="data?.copyrightAndGeneralInformation" class="mobile-only text-1.5 leading-2 font-sans mt-auto uppercase lg:mt-6" />
        </div>
        <div class="col-span-3 space-y-1 lg:col-span-7 lg:col-start-6">
          <nav>
            <ul v-if="(data?.links.length || 0) + (data?.bottomLinks.length || 0) > 0" class="flex flex-col items-end lg:flex-row lg:gap-11 lg:items-center lg:justify-end">
              <li v-for="link in data?.links" :key="link!.id">
                <ButtonNav :link="link?.customLink" class="typo-sans-1 uppercase block!" />
              </li>
              <li v-for="link in data?.bottomLinks" :key="link!.id" class="mobile-only">
                <ButtonNav :link="link?.customLink" class="typo-sans-1 uppercase block!" />
              </li>
            </ul>
          </nav>
          <div class="desktop-only">
            <div class="flex items-baseline justify-end">
              <Text
                v-if="data?.copyrightAndGeneralInformation"
                :html="data?.copyrightAndGeneralInformation"
                class="text-2 leading-none font-sans uppercase"
              />
              <span class="text-2 leading-none font-sans uppercase">&nbsp;|&nbsp;</span>
              <nav>
                <ul v-if="(data?.bottomLinks.length || 0) > 0" class="flex gap-1 items-center">
                  <li v-for="link, index in data?.bottomLinks" :key="link!.id">
                    <ButtonNav :link="link?.customLink" class="text-2 leading-3 font-sans uppercase" />
                    <span v-if="index < (data?.bottomLinks.length || 0) - 1" class="text-2 leading-none font-sans uppercase">&nbsp;|</span>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<style scoped>
.theFooter :deep(.buttonNav.router-link-exact-active) {
  color: inherit;

  &::after {
    transform: scaleX(0);
    transform-origin: right;
    transition: transform 0.32s var(--ease-custom-circ);
  }
}

@media (hover: hover) and (pointer: fine) {
  .theFooter :deep(.buttonNav.router-link-exact-active:is(:hover, :focus-within))::after {
    transform: scaleX(1);
    transform-origin: left;
  }
}
</style>
